import groupBy from 'lodash/groupBy';

import {
  setShouldFetchStyleboardInfo,
  getCurrentStyleboardInfo
} from 'redux/PPO';

import {
  createEmptyCustomProp,
  deleteCustomProp,
  getCustomPropsFor,
  notifyCustomFurnitureStepCompleteFor,
  placeOrder,
  submitCustomProps,
  updateCart,
  updateCustomProp,
  uploadCustomPropImages
} from './api';

import { CustomProp } from './models';

// constants
export const reducerKey = 'customProps';

// actions/thunks
export const loadCustomProps = ({ spaceId }) => dispatch => {
  return getCustomPropsFor(spaceId)
    .then(customProps => {
      // Convert server response to internal CustomProps model.
      return customProps.map(server => {
        const client = new CustomProp({
          id: server.id,
          spaceId: server.space.toString(),
          height: server.height,
          width: server.width,
          depth: server.depth,
          retailerUrl: server.url,
          description: server.description,
          status: server.status,
          units: server.unit,
          orderNumber: server.orderNumber,
          orderPrice: parseFloat(server.price)
        });
        return client;
      });
    })
    .then(customProps => {
      dispatch({
        type: 'SET_CUSTOM_PROPS',
        payload: { spaceId, customProps }
      });
    });
};

export const addCustomProp = customProp => dispatch => {
  return createEmptyCustomProp(customProp)
    .then(customPropId => {
      customProp.setId({ customPropId });
    })
    .then(() => {
      return updateCustomProp(customProp);
    })
    .then(() => {
      return (customProp.frontImage() || customProp.sideImage())
        ? uploadCustomPropImages(customProp)
        : Promise.resolve();
    })
    .then(() => {
      return dispatch({
        type: 'ADD_CUSTOM_PROP',
        payload: customProp
      });
    });
};

export const submitProps = (orderProps, orderData) => dispatch => {
  dispatch({
    type: 'SET_ORDER_NUMBER',
    payload: orderData.orderNumber
  });

  return submitCustomProps(orderProps, orderData)
    .then(() => {
      // Refresh the custom prop list after server submission.
      return dispatch(loadCustomProps({ spaceId: orderProps[0].spaceId() }));
    });
};

export const placeAllFreePropsOrder = (orderProps, freePropPackage) => dispatch => {
  return updateCart({ orderProps, freePropPackage, promoCode: '' })
    .then(({ priceData, orderItems }) => {
      return placeOrder({
        displayedInfo: priceData,
        orderItems
      });
    })
    .then(response => {
      const { data } = response;
      if (!data || data.ok === false) {
        throw new Error((data && data.errorMessage) || 'Unknown error');
      }

      const orderData = data;
      return orderData;
    })
    .then(orderData => {
      return dispatch(submitProps(orderProps, orderData));
    });
};

export const markStepComplete = ({ spaceId }) => dispatch => {
  const styleboardInfo = dispatch(getCurrentStyleboardInfo());
  const styleboardInfoId = styleboardInfo.id;
  return notifyCustomFurnitureStepCompleteFor({ spaceId, styleboardInfoId }).then(() => {
    // we just want to tell other components they should request styleboard info here
    // otherwise parent components show a loader instead of rendering children
    // causing CustomPieces to remount which messes things up when completing a payment
    // Dashboard should request the styleboard info when we get back there
    // so status should update properly
    dispatch(setShouldFetchStyleboardInfo(true));
  });
};

export const updateSoftDelete = ({ spaceId, customPropId, softDelete }) => dispatch => {
  // Note: the logic here is now HARD delete, it used to be soft delete.
  // Leaving name and feature in for now as we might want to re-enable
  // soft delete support in the future.

  // Delete custom prop from server.
  deleteCustomProp(customPropId, spaceId);

  // Delete custom prop from local state.
  return dispatch({
    type: 'SET_SOFT_DELETE_STATUS',
    payload: { spaceId, customPropId }
  });
};

// ======================================================
// reducer - map of spaceId to CustomProp model instances
// ======================================================
export const reducer = (state = {}, action) => {
  switch (action.type) {
    case 'SET_CUSTOM_PROPS': {
      const { spaceId, customProps } = action.payload;
      return { ...state, [spaceId]: customProps };
    }
    case 'ADD_CUSTOM_PROP': {
      const customProp = action.payload;
      const spaceId = customProp.spaceId();
      const customPropsForSpace = state[spaceId] || [];
      return { ...state, [spaceId]: [...customPropsForSpace, customProp] };
    }
    case 'SET_SOFT_DELETE_STATUS': {
      const { customPropId, spaceId } = action.payload;
      const customProps = state[spaceId] || [];

      return {
        ...state,
        [spaceId]: customProps.filter(prop => prop.id() !== customPropId)
      };
    }
    case 'SET_ORDER_NUMBER': {
      return { ...state, orderNumber: action.payload };
    }
    default:
      return state;
  }
};

// =========
// Selectors
// =========
/** Returns the last successful order number */
export const getOrderNumber = state => {
  return state[reducerKey].orderNumber;
};

/**
 * Returns all the custom props for a space.
 */
export const getCustomProps = (state, spaceId) => {
  return state[reducerKey][spaceId] || [];
};

/**
 * Returns the cart props.
 */
export const getCartProps = (state, spaceId) => {
  const allProps = getCustomProps(state, spaceId);

  // Only include props that have not already been submitted.
  const cartProps = allProps.filter(prop => {
    const status = prop.status();
    return status === CustomProp.Status.NOT_STARTED ||
      status === CustomProp.Status.FIND_ALTERNATIVE_NOT_SUBMITTED;
  });

  return cartProps;
};

/**
 * Returns the order props.
 */
export const getOrderProps = (state, spaceId) => {
  const cartProps = getCartProps(state, spaceId);

  // Only include cart props that have not been deleted.
  const orderProps = cartProps.filter(prop => !prop.softDelete());

  return orderProps;
};

/**
 * Returns props that have already been submitted
 * grouped by order number.
 */
export const getSubmittedProps = (state, spaceId) => {
  const allProps = getCustomProps(state, spaceId);

  // Only include props that have already been submitted
  // AND have an order number.
  const submittedProps = allProps.filter(prop => {
    const status = prop.status();
    return status !== CustomProp.Status.NOT_STARTED &&
      status !== CustomProp.Status.FIND_ALTERNATIVE_NOT_SUBMITTED;
  });

  // grouped by order number
  const grouped = groupBy(submittedProps, prop => prop.orderNumber());
  return grouped;
};

/**
 * Computes the total price for an array of props.
 * Props with softDelete true are skipped in calculation.
 */
export const computeTotal = (customProps, customPropsPrice) => {
  const total = customProps.reduce((sum, prop) => {
    const price = prop.orderPrice()
      ? parseFloat(prop.orderPrice())
      : (prop.softDelete() || prop.isFindAlternative()) ? 0 : parseFloat(prop.orderPrice());

    return sum + price;
  }, 0);

  return total;
};
